import { Suspense } from 'react'
import * as THREE from 'three/webgpu'
import { Canvas } from '@react-three/fiber/webgpu'
import { flags, webgpuAvailable } from './config/flags'
import { perf, pushError } from './debug/perf'
import { PerfProbe } from './debug/PerfProbe'
import { Hud } from './debug/Hud'
import { TestRoom } from './scene/TestRoom'
import { CameraRig } from './scene/CameraRig'
import { PostFX } from './scene/PostFX'
import { AdaptiveQuality } from './scene/AdaptiveQuality'

const toneMappers = {
  agx: THREE.AgXToneMapping,
  neutral: THREE.NeutralToneMapping,
  aces: THREE.ACESFilmicToneMapping,
  none: THREE.NoToneMapping,
} as const

async function readAdapterLabel() {
  if (!webgpuAvailable || flags.forceWebGL) return
  try {
    const adapter = await navigator.gpu.requestAdapter()
    const info = adapter?.info
    if (info) perf.gpuLabel = [info.vendor, info.architecture].filter(Boolean).join(' ')
  } catch {
    /* adapter info is optional */
  }
}

export function App() {
  return (
    <>
      <Canvas
        // One WebGPURenderer for both paths. On browsers without WebGPU (or with ?webgl)
        // three switches to its WebGL 2 backend; every TSL material compiles to GLSL there.
        renderer={{
          antialias: true,
          powerPreference: 'high-performance',
          forceWebGL: flags.forceWebGL,
          toneMapping: toneMappers[flags.tone],
          toneMappingExposure: 1.0,
          // GPU timing for the HUD. Off with ?hud=0 so screenshots/final builds pay nothing.
          trackTimestamp: flags.hud,
        }}
        dpr={[Math.min(1, flags.maxDpr), flags.maxDpr]}
        camera={{ fov: 50, near: 0.1, far: 120, position: [0, 1.8, 9.5] }}
        onCreated={(state) => {
          const isWebGPU = 'isWebGPUBackend' in state.renderer.backend
          perf.backend = isWebGPU ? 'WebGPU' : flags.forceWebGL ? 'WebGL 2 (forced)' : 'WebGL 2 (fallback)'
          void readAdapterLabel()
        }}
        onError={(e: unknown) => pushError(`canvas: ${String(e)}`)}
      >
        <Suspense fallback={null}>
          <TestRoom />
        </Suspense>
        <CameraRig enabled={flags.orbit} />
        <PostFX />
        <AdaptiveQuality />
        <PerfProbe />
      </Canvas>
      {flags.hud && <Hud />}
    </>
  )
}
